import { createContext, useContext, useState, useEffect, useCallback } from 'react';

const BASE_URL = process.env.REACT_APP_API_BASE_URL || 'http://localhost:3100';
const API_PREFIX = '/api';

const AuthContext = createContext(null);

/**
 * AuthProvider - Manages authentication state for the application
 * Provides user data, loading state, and auth methods to all children
 */
export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [loggingOut, setLoggingOut] = useState(false);
  const [error, setError] = useState(null);

  /**
   * Check authentication status by calling /auth/me
   * Called on mount and after OAuth callback
   */
  const checkAuth = useCallback(async () => {
    try {
      setIsLoading(true);
      setError(null);

      const response = await fetch(`${BASE_URL}${API_PREFIX}/auth/me?_=${Date.now()}`, {
        credentials: 'include',
      });

      if (!response.ok) {
        // Non-200 response means not authenticated
        setUser(null);
        return;
      }

      const data = await response.json();

      if (data.authenticated && data.user) {
        setUser(data.user);
      } else {
        setUser(null);
      }
    } catch (err) {
      console.error('Auth check failed:', err);
      setError('Unable to connect to server.');
      setUser(null);
    } finally {
      setIsLoading(false);
    }
  }, []);

  // Check auth status on mount
  useEffect(() => {
    checkAuth();
  }, [checkAuth]);

  /**
   * Redirect to Microsoft OAuth login
   * Backend handles the OAuth flow and redirects back
   */
  const loginWithMicrosoft = useCallback(() => {
    window.location.href = `${BASE_URL}${API_PREFIX}/auth/microsoft`;
  }, []);

  /**
   * Logout and destroy session
   * Clears user state and redirects to login
   */
  const logout = useCallback(async () => {
    setLoggingOut(true);
    try {
      setError(null);

      await fetch(`${BASE_URL}${API_PREFIX}/auth/logout`, {
        method: 'POST',
        credentials: 'include',
      });

      setUser(null);
    } catch (err) {
      console.error('Logout failed:', err);
      // Still clear user state even if logout API fails
      setUser(null);
    } finally {
      setLoggingOut(false);
    }
  }, []);

  /**
   * Clear any error state
   */
  const clearError = useCallback(() => {
    setError(null);
  }, []);

  const value = {
    user,
    isLoading,
    loggingOut,
    error,
    isAuthenticated: !!user,
    loginWithMicrosoft,
    logout,
    refreshUser: checkAuth,
    clearError,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

/**
 * useAuth hook - Access auth context from any component
 * Must be used within an AuthProvider
 */
export const useAuth = () => {
  const context = useContext(AuthContext);

  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }

  return context;
};

export default AuthContext;
